import type {
  AdminMe,
  AdminMeta,
  AuditListResponse,
  CatalogCategory,
  CatalogSeries,
  CatalogSeriesDetail,
  CatalogSync,
  EntitlementRequest,
  UserLine,
  LoginResult,
  UserDetail,
  UserListResponse,
} from './types'

// 空基址：开发走 Vite 代理，生产与 API 同域。
const BASE = import.meta.env.VITE_API_BASE ?? ''

/** 后端信封 {error:{code,message,request_id}} */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId: string

  constructor(status: number, code: string, message: string, requestId = '') {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.requestId = requestId
  }

  /** 401 / invalid_token。登录 403 不是会话失效。 */
  get isSessionInvalid(): boolean {
    return this.status === 401 || this.code === 'invalid_token'
  }
}

type TokenReader = () => string | null
type SessionExpiredHandler = () => void

let readToken: TokenReader = () => null
let onSessionExpired: SessionExpiredHandler = () => {}

/** 由 session 注入，避免循环依赖。 */
export function configureClient(opts: { getToken: TokenReader; onExpired: SessionExpiredHandler }) {
  readToken = opts.getToken
  onSessionExpired = opts.onExpired
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { skipAuth?: boolean },
): Promise<T> {
  const headers: Record<string, string> = {}
  const token = opts?.skipAuth ? null : readToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'network_error', '无法连接到服务，请检查网络或后端是否在运行')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!res.ok) {
    const envelope = (payload as { error?: { code?: string; message?: string; request_id?: string } })?.error
    const err = new ApiError(
      res.status,
      envelope?.code ?? 'unknown_error',
      envelope?.message ?? `请求失败（HTTP ${res.status}）`,
      envelope?.request_id ?? '',
    )
    // 带票的 forbidden = 后台会话被降权
    if (err.isSessionInvalid || (token != null && err.code === 'forbidden')) onSessionExpired()
    throw err
  }

  return payload as T
}

const qs = (params: Record<string, string | number | undefined>) => {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResult>('POST', '/api/v1/admin/auth/login', { email, password }, { skipAuth: true }),
  logout: () => request<{ message: string }>('POST', '/api/v1/admin/auth/logout'),
  me: () => request<AdminMe>('GET', '/api/v1/admin/me'),
  meta: () => request<AdminMeta>('GET', '/api/v1/admin/meta'),

  listUsers: (query: string, limit = 50, offset = 0) =>
    request<UserListResponse>('GET', `/api/v1/admin/users${qs({ query, limit, offset })}`),
  userDetail: (id: string) => request<UserDetail>('GET', `/api/v1/admin/users/${encodeURIComponent(id)}`),
  grantEntitlement: (id: string, body: EntitlementRequest) =>
    request<UserLine>('PUT', `/api/v1/admin/users/${encodeURIComponent(id)}/entitlement`, body),
  revokeEntitlement: (id: string) =>
    request<UserLine>('DELETE', `/api/v1/admin/users/${encodeURIComponent(id)}/entitlement`),

  listAudit: (limit = 50, offset = 0) =>
    request<AuditListResponse>('GET', `/api/v1/admin/audit${qs({ limit, offset })}`),

  catalogStatus: () => request<CatalogSync>('GET', '/api/v1/admin/catalog'),
  syncCatalog: () => request<CatalogSync>('POST', '/api/v1/admin/catalog/sync'),
  catalogCategories: () => request<{ categories: CatalogCategory[] }>('GET', '/api/v1/catalog/categories'),
  catalogSeries: (categoryId: string) =>
    request<{ series: CatalogSeries[]; total: number }>(
      'GET',
      `/api/v1/catalog/categories/${encodeURIComponent(categoryId)}/series`,
    ),
  catalogSeriesDetail: (seriesId: string) =>
    request<CatalogSeriesDetail>('GET', `/api/v1/catalog/series/${encodeURIComponent(seriesId)}`),
}
